import React, { useState, useEffect, useCallback } from 'react';
import { signOut, onAuthStateChanged } from '../firebase/authService';
import { 
  subscribeToQuotes, 
  updateQuoteStatus, 
  deleteQuote, 
  getQuoteStats,
  updateQuoteWithQuotation,
  QUOTE_STATUSES, 
  STATUS_LABELS, 
  STATUS_COLORS 
} from '../firebase/quotesService';
import QuotationList from '../Components/Quotation/QuotationList';
import QuotationForm from '../Components/Quotation/QuotationForm';
import InvoiceList from '../Components/Invoice/InvoiceList';
import JobScheduleList from '../Components/Schedule/JobScheduleList';
import EmployeeManagement from '../Components/Admin/EmployeeManagement';
import AttendanceMonitoring from '../Components/Admin/AttendanceMonitoring';
import DateRangeFilter from '../Components/Shared/DateRangeFilter';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('quotes');
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [expandedQuote, setExpandedQuote] = useState(null);
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [selectedQuoteForQuotation, setSelectedQuoteForQuotation] = useState(null);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null, label: 'This Month' });
  const [quotesUnsubscribe, setQuotesUnsubscribe] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged((user) => {
      setUser(user);
    });

    return () => unsubscribe();
  }, []);

  // Handle date range changes and update subscriptions
  const handleDateRangeChange = useCallback((newDateRange) => {
    console.log('🗓️ Date range changed:', {
      label: newDateRange.label,
      startDate: newDateRange.startDate,
      endDate: newDateRange.endDate,
      startDateString: newDateRange.startDate?.toISOString(),
      endDateString: newDateRange.endDate?.toISOString()
    });
    
    setDateRange(newDateRange);
    
    if (user) {
      // Unsubscribe from previous subscription
      if (quotesUnsubscribe) {
        console.log('🔄 Unsubscribing from previous quotes subscription');
        quotesUnsubscribe();
      }
      
      // Create new subscription with date range
      const options = {};
      if (newDateRange.startDate) {
        options.startDate = newDateRange.startDate;
      }
      if (newDateRange.endDate) {
        options.endDate = newDateRange.endDate;
      }
      
      console.log('📊 Creating new subscription with options:', options);
      
      const newUnsubscribe = subscribeToQuotes((quotesData) => {
        console.log('📥 Received quotes data:', {
          count: quotesData.length,
          firstQuote: quotesData[0] ? {
            id: quotesData[0].id,
            createdAt: quotesData[0].createdAt,
            customerName: quotesData[0].customerName
          } : null
        });
        setQuotes(quotesData);
        setFilteredQuotes(quotesData);
        setLoading(false);
      }, options);
      
      setQuotesUnsubscribe(() => newUnsubscribe);
      
      // Update stats with date range
      console.log('📈 Updating stats with options:', options);
      getQuoteStats(options).then((statsData) => {
        console.log('📊 Received stats:', statsData);
        setStats(statsData);
      });
    }
  }, [user]);

  useEffect(() => {
    if (user && !dateRange.startDate && !dateRange.endDate) {
      // Initialize with default date range (This Month) only once when user is available and no date range is set
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      
      const initialDateRange = {
        startDate: startOfMonth,
        endDate: endOfMonth,
        label: `This Month`
      };
      
      handleDateRangeChange(initialDateRange);
    }

    return () => {
      if (quotesUnsubscribe) {
        quotesUnsubscribe();
      }
    };
  }, [user, dateRange.startDate, dateRange.endDate, handleDateRangeChange]);

  useEffect(() => {
    // Filter quotes based on search term and status
    let filtered = quotes;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(quote =>
        quote.customerName.toLowerCase().includes(searchLower) ||
        quote.customerEmail.toLowerCase().includes(searchLower) ||
        quote.companyName.toLowerCase().includes(searchLower)
      );
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(quote => quote.status === selectedStatus);
    }

    setFilteredQuotes(filtered);
  }, [quotes, searchTerm, selectedStatus]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    const success = await updateQuoteStatus(quoteId, newStatus);
    if (success) {
      // Update stats after status change with current date range
      const options = {};
      if (dateRange.startDate) {
        options.startDate = dateRange.startDate;
      }
      if (dateRange.endDate) {
        options.endDate = dateRange.endDate;
      }
      getQuoteStats(options).then(setStats);
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (window.confirm('Are you sure you want to delete this quote?')) {
      const success = await deleteQuote(quoteId);
      if (success) {
        // Update stats after deletion with current date range
        const options = {};
        if (dateRange.startDate) {
          options.startDate = dateRange.startDate;
        }
        if (dateRange.endDate) {
          options.endDate = dateRange.endDate;
        }
        getQuoteStats(options).then(setStats);
      }
    }
  };

  const toggleQuoteExpansion = (quoteId) => {
    setExpandedQuote(expandedQuote === quoteId ? null : quoteId);
  };

  const handleCreateQuotation = (quote) => {
    setSelectedQuoteForQuotation(quote);
    setShowQuotationModal(true);
  };

  const handleQuotationSaved = async (quotationData) => {
    try {
      // Update quote request with quotation information
      if (selectedQuoteForQuotation && quotationData) {
        await updateQuoteWithQuotation(
          selectedQuoteForQuotation.id, 
          quotationData.quotationNumber, 
          quotationData.id
        );
        // Refresh stats after status change with current date range
        const options = {};
        if (dateRange.startDate) {
          options.startDate = dateRange.startDate;
        }
        if (dateRange.endDate) {
          options.endDate = dateRange.endDate;
        }
        getQuoteStats(options).then(setStats);
      }
      
      handleQuotationModalClose();
    } catch (error) {
      console.error('Error updating quote with quotation:', error);
    }
  };

  const handleQuotationModalClose = () => {
    setShowQuotationModal(false);
    setSelectedQuoteForQuotation(null);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (typeof date === 'string') return date;
    return date.toLocaleDateString('en-SG', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatProducts = (products) => {
    if (!products || !Array.isArray(products)) return 'N/A';
    return products.map(product => {
      const workingHeight = product.workingHeight;
      const heightDisplay = (typeof workingHeight === 'number' && workingHeight > 0) || (typeof workingHeight === 'string' && workingHeight !== '')
        ? `${workingHeight} m` 
        : 'N/A';
      return `${product.quantity || 'N/A'}x ${product.width || 'N/A'} width, ${heightDisplay} working height`;
    }).join('; ');
  };

  const formatServices = (services) => {
    if (!services || typeof services !== 'object') return null;
    
    const servicesList = [];
    
    if (services.transportation) {
      servicesList.push('✓ Delivery and Collection (Transportation)');
    }
    
    if (services.assemblyDismantle) {
      servicesList.push('✓ Assemble and Dismantle');
    }
    
    if (services.endorsement) {
      servicesList.push('✓ Endorsement (Weekly)');
    }

    if (servicesList.length === 0) {
      return null;
    }

    return {
      services: servicesList,
      siteAddress: services.siteAddress || ''
    };
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdowns = document.querySelectorAll('.dropdown-menu');
      dropdowns.forEach(dropdown => {
        if (!dropdown.parentElement.contains(event.target)) {
          dropdown.style.display = 'none';
        }
      });
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>Admin Dashboard</h1>
          <div className="header-actions">
            <DateRangeFilter 
              onDateRangeChange={handleDateRangeChange}
              className="header-date-filter"
            />
            <div className="user-dropdown" style={{ position: 'relative', display: 'inline-block' }}>
              <span 
                className="user-icon" 
                style={{ cursor: 'pointer', fontSize: '20px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  const dropdown = e.target.nextElementSibling;
                  dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                }}
              >
                👤
              </span>
              <div 
                className="dropdown-menu" 
                style={{
                  display: 'none',
                  position: 'absolute',
                  right: 0,
                  top: '100%',
                  backgroundColor: 'white',
                  minWidth: '160px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  borderRadius: '4px',
                  zIndex: 1000,
                  border: '1px solid #d9d9d9'
                }}
              >
                <div 
                  style={{
                    padding: '8px 16px',
                    borderBottom: '1px solid #f0f0f0',
                    fontWeight: 'bold',
                    color: '#1890ff',
                    fontSize: '12px'
                  }}
                >
                  {user?.email}
                </div>
                <div 
                  style={{
                    padding: '8px 16px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                >
                  Profile
                </div>
                <div 
                  style={{
                    padding: '8px 16px',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  onClick={handleSignOut}
                >
                  Logout
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <nav className="dashboard-nav">
        <button 
          className={`nav-btn ${activeTab === 'quotes' ? 'active' : ''}`}
          onClick={() => setActiveTab('quotes')}
        >
          Quote Requests
        </button>
        <button 
          className={`nav-btn ${activeTab === 'quotations' ? 'active' : ''}`}
          onClick={() => setActiveTab('quotations')}
        >
          Generate Quotations
        </button>
        <button 
          className={`nav-btn ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          Invoice Management
        </button>
        <button 
          className={`nav-btn ${activeTab === 'schedule' ? 'active' : ''}`}
          onClick={() => setActiveTab('schedule')}
        >
          Job Schedule
        </button>
        <button 
          className={`nav-btn ${activeTab === 'employees' ? 'active' : ''}`}
          onClick={() => setActiveTab('employees')}
        >
          Employee Management
        </button>
        <button 
          className={`nav-btn ${activeTab === 'attendance' ? 'active' : ''}`}
          onClick={() => setActiveTab('attendance')}
        >
          Attendance Monitoring
        </button>
        <button 
          className={`nav-btn ${activeTab === 'catalogue' ? 'active' : ''}`}
          onClick={() => setActiveTab('catalogue')}
        >
          Catalogue Downloads
        </button>
      </nav>

      <main className="dashboard-content">
        {/* Mobile Date Filter Section - appears on all tabs */}
        <div className="mobile-filter-section">
          <DateRangeFilter 
            onDateRangeChange={handleDateRangeChange}
            className="mobile-date-filter"
          />
        </div>

        {activeTab === 'quotes' && (
          <div className="content-section">
            <div className="section-header-redesigned">
              <div className="header-top">
                <h2>Quote Requests Management</h2>
              </div>
              
              <div className="stats-summary">
                <div className="stat-item">
                  <span className="stat-number">{stats.total || 0}</span>
                  <span className="stat-label">Total Quotes</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number" style={{color: STATUS_COLORS.new}}>{stats.new || 0}</span>
                  <span className="stat-label">New</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number" style={{color: STATUS_COLORS.contacted}}>{stats.contacted || 0}</span>
                  <span className="stat-label">Contacted</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number" style={{color: STATUS_COLORS.quoted}}>{stats.quoted || 0}</span>
                  <span className="stat-label">Quoted</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number" style={{color: STATUS_COLORS.closed}}>{stats.closed || 0}</span>
                  <span className="stat-label">Closed</span>
                </div>
              </div>
            </div>

            {/* Mobile Stats for Quote Requests */}
            <div className="quote-stats-mobile">
              <div className="quote-stat-mobile">
                <span className="quote-stat-number">{stats.total || 0}</span>
                <span className="quote-stat-label">Total</span>
              </div>
              <div className="quote-stat-mobile">
                <span className="quote-stat-number" style={{color: STATUS_COLORS.new}}>{stats.new || 0}</span>
                <span className="quote-stat-label">New</span>
              </div>
              <div className="quote-stat-mobile">
                <span className="quote-stat-number" style={{color: STATUS_COLORS.contacted}}>{stats.contacted || 0}</span>
                <span className="quote-stat-label">Contacted</span>
              </div>
              <div className="quote-stat-mobile">
                <span className="quote-stat-number" style={{color: STATUS_COLORS.quoted}}>{stats.quoted || 0}</span>
                <span className="quote-stat-label">Quoted</span>
              </div>
              <div className="quote-stat-mobile">
                <span className="quote-stat-number" style={{color: STATUS_COLORS.closed}}>{stats.closed || 0}</span>
                <span className="quote-stat-label">Closed</span>
              </div>
            </div>

            <div className="filters-section">
              <div className="search-filter">
                <input
                  type="text"
                  placeholder="Search by customer name, email, or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              <div className="status-filter">
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="status-select"
                >
                  <option value="all">All Status</option>
                  <option value={QUOTE_STATUSES.NEW}>New</option>
                  <option value={QUOTE_STATUSES.CONTACTED}>Contacted</option>
                  <option value={QUOTE_STATUSES.QUOTED}>Quoted</option>
                  <option value={QUOTE_STATUSES.CLOSED}>Closed</option>
                </select>
              </div>
            </div>

            {filteredQuotes.length === 0 ? (
              <div className="no-data">
                <p>No quotes found. {searchTerm || selectedStatus !== 'all' ? 'Try adjusting your search or filter criteria.' : 'Quotes will appear here once customers submit requests.'}</p>
              </div>
            ) : (
              <div className="quotes-list">
                {filteredQuotes.map((quote) => (
                  <div key={quote.id} className="quote-card">
                    <div className="quote-header" onClick={() => toggleQuoteExpansion(quote.id)}>
                  <div className="quote-basic-info">
                    <h3>{quote.customerName}</h3>
                    <p className="quote-company">{quote.companyName}</p>
                    <div className="quote-meta">
                      <span className="quote-date">{formatDate(quote.createdAt)}</span>
                      <span 
                        className="quote-status"
                        style={{
                          backgroundColor: STATUS_COLORS[quote.status],
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      >
                        {STATUS_LABELS[quote.status]}
                      </span>
                    </div>
                  </div>
                  <div className="quote-summary">
                    <p><strong>Type:</strong> {quote.enquiryType}</p>
                    <p><strong>Products:</strong> {formatProducts(quote.products)}</p>
                    <p><strong>Email:</strong> {quote.customerEmail}</p>
                  </div>
                  <div className="quote-actions-header">
                    {quote.quotationNumber ? (
                      <div className="quotation-number-display">
                        <strong style={{ color: '#389e0d' }}>
                          Quotation: {quote.quotationNumber}
                        </strong>
                      </div>
                    ) : (
                      quote.status !== QUOTE_STATUSES.CLOSED && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCreateQuotation(quote);
                          }}
                          className="create-quotation-btn-header"
                        >
                          Create Quotation
                        </button>
                      )
                    )}
                    <div className="expand-icon">
                      {expandedQuote === quote.id ? '▼' : '▶'}
                    </div>
                  </div>
                    </div>

                    {expandedQuote === quote.id && (
                      <div className="quote-details">
                        <div className="quote-full-info">
                          <div className="info-section">
                            <h4>Customer Information</h4>
                            <p><strong>Name:</strong> {quote.customerName}</p>
                            <p><strong>Email:</strong> {quote.customerEmail}</p>
                            <p><strong>Phone:</strong> {quote.customerPhone}</p>
                            <p><strong>Company:</strong> {quote.companyName}</p>
                          </div>
                          
                          <div className="info-section">
                            <h4>Enquiry Details</h4>
                            <p><strong>Type:</strong> {quote.enquiryType}</p>
                            <p><strong>Products:</strong> {formatProducts(quote.products)}</p>
                            
                            {quote.enquiryType === 'purchase' && (
                              <>
                                {quote.datePurchase && quote.datePurchase !== '' ? (
                                  <p><strong>Purchase Date:</strong> {quote.datePurchase}</p>
                                ) : (quote.purchaseCheckbox === 'yes' || quote.purchaseCheckbox === true) ? (
                                  <p><strong>Purchase Date:</strong> No fixed date</p>
                                ) : (
                                  <p><strong>Purchase Date:</strong> Not specified</p>
                                )}
                              </>
                            )}
                            
                            {quote.enquiryType === 'rental' && (
                              <>
                                {quote.dateRental && Array.isArray(quote.dateRental) && quote.dateRental.length === 2 && quote.dateRental[0] !== '' ? (
                                  <p><strong>Rental Period:</strong> {quote.dateRental.join(' to ')}</p>
                                ) : (quote.dateDuration && quote.durationType) ? (
                                  <p><strong>Duration:</strong> {quote.dateDuration} {quote.durationType}{quote.dateDuration > 1 ? 's' : ''}</p>
                                ) : (
                                  <p><strong>Rental Period:</strong> Not specified</p>
                                )}
                              </>
                            )}
                          </div>

                          {(() => {
                            const servicesInfo = formatServices(quote.services);
                            return servicesInfo && (
                              <div className="info-section">
                                <h4>Services</h4>
                                {servicesInfo.services.map((service, index) => (
                                  <p key={index} style={{ margin: '4px 0', color: '#52c41a' }}>{service}</p>
                                ))}
                                {servicesInfo.siteAddress && (
                                  <p><strong>Site Address:</strong> {servicesInfo.siteAddress}</p>
                                )}
                              </div>
                            );
                          })()}

                          {quote.message && quote.message !== 'N/A' && (
                            <div className="info-section">
                              <h4>Customer Message</h4>
                              <p className="customer-message">{quote.message}</p>
                            </div>
                          )}

                          <div className="info-section">
                            <h4>System Information</h4>
                            <p><strong>Submitted:</strong> {quote.timestamp}</p>
                            <p><strong>Email Sent:</strong> {quote.emailSent ? 'Yes' : 'No'}</p>
                            <p><strong>Quote ID:</strong> {quote.id}</p>
                          </div>
                        </div>

                        <div className="quote-actions">
                          <div className="status-update">
                            <label>Update Status:</label>
                            <select
                              value={quote.status}
                              onChange={(e) => handleStatusChange(quote.id, e.target.value)}
                              className="status-select-action"
                            >
                              <option value={QUOTE_STATUSES.NEW}>New</option>
                              <option value={QUOTE_STATUSES.CONTACTED}>Contacted</option>
                              <option value={QUOTE_STATUSES.QUOTED}>Quoted</option>
                              <option value={QUOTE_STATUSES.CLOSED}>Closed</option>
                            </select>
                          </div>
                          
                          {quote.quotationNumber ? (
                            <div style={{
                              backgroundColor: '#f6ffed',
                              border: '1px solid #b7eb8f',
                              borderRadius: '4px',
                              padding: '8px 16px',
                              marginRight: '8px',
                              display: 'inline-block'
                            }}>
                              <strong style={{ color: '#389e0d' }}>
                                Quotation: {quote.quotationNumber}
                              </strong>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleCreateQuotation(quote)}
                              className="create-quotation-btn"
                              style={{
                                backgroundColor: '#1890ff',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                marginRight: '8px'
                              }}
                            >
                              Create Quotation
                            </button>
                          )}
                          
                          <button
                            onClick={() => handleDeleteQuote(quote.id)}
                            className="delete-btn"
                          >
                            Delete Quote
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="system-info">
              <div className="info-card">
                <h3>📧 Email Integration Active</h3>
                <p>
                  This dashboard stores and manages all quote requests while maintaining the email notification system. 
                  Every quote is automatically emailed to <strong>contact@beemascaffolding.sg</strong> and stored in the database for tracking.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'quotations' && (
          <div className="content-section">
            <QuotationList 
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
            />
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="content-section">
            <InvoiceList 
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
            />
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="content-section">
            <JobScheduleList 
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
            />
          </div>
        )}

        {activeTab === 'employees' && (
          <div className="content-section">
            <EmployeeManagement />
          </div>
        )}

        {activeTab === 'attendance' && (
          <div className="content-section">
            <AttendanceMonitoring 
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
            />
          </div>
        )}

        {activeTab === 'catalogue' && (
          <div className="content-section">
            <h2>Catalogue Downloads</h2>
            <div className="info-card">
              <h3>Email Notification System</h3>
              <p>
                Catalogue download requests are currently sent directly to <strong>contact@beemascaffolding.sg</strong> via email. 
                Each request includes customer name, contact details, and company information.
              </p>
              <p>
                <strong>Next Steps:</strong> To track catalogue downloads in this dashboard, you would need to:
              </p>
              <ul>
                <li>Store download requests in Firestore database</li>
                <li>Add analytics for download tracking</li>
                <li>Implement customer contact management</li>
                <li>Create follow-up campaign features</li>
              </ul>
            </div>
          </div>
        )}

      </main>

      {/* Quotation Modal */}
      {showQuotationModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={handleQuotationModalClose}
              style={{
                position: 'absolute',
                top: '10px',
                right: '15px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ×
            </button>
            <h2 style={{ marginBottom: '20px' }}>Create Quotation from Quote Request</h2>
            <QuotationForm 
              linkedQuoteRequest={selectedQuoteForQuotation}
              onSave={handleQuotationSaved}
              onCancel={handleQuotationModalClose}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
